import React, {ReactNode, useMemo, useState} from 'react';

import {createSelector} from '@reduxjs/toolkit';
import jquery from 'jquery';
import {colors} from 'js-libs/sweetcommons/styles';
import Joyride, {ACTIONS, CallBackProps, EVENTS} from 'react-joyride';
import {DefaultRootState, useSelector} from 'react-redux';
import {createGlobalStyle} from 'styled-components';

import {
  CurrentUserSelector,
  useUpdateUserTourStatus,
} from 'app/current-user/api';
import {ErrorBoundary} from 'app/general/structures/ErrorBoundary';
import {getBrowser} from 'app/utils/browser';
import {gt} from 'app/utils/i18n';
import {reportToSentry} from 'app/utils/sentry';

import {
  AuthenticatedUserSerializedType,
  UserAvailableTours,
  UserTourType,
} from 'sweet-types';

export interface TourStep<D = any> {
  role: 'manager' | 'teammate' | 'any';
  title: ReactNode;
  content: ReactNode;
  target: string;
  placement?:
    | 'center'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'auto';
  condition?: (additionalData: D) => boolean;
  disableBeacon?: boolean;
  spotlightPadding?: number;
  highlight?: string;
}

interface OwnProps<D> {
  tourname: UserAvailableTours;
  additionalData?: D;
  steps: Array<TourStep<D>>;
}

const TOUR_VERSION = 0;

const STEP_DEFAULT_PROPS = {
  disableBeacon: true,
  placement: 'auto' as const,
};

export function BaseTour<D>({
  tourname,
  additionalData,
  steps,
}: OwnProps<D>): JSX.Element | null {
  const tourversion = TOUR_VERSION;

  const {status} = useSelector((state) => tourSelector(state, tourname));
  const userRole = useSelector(userRoleSelector);
  const [updateUserTourStatus, tourUpdateStatus] = useUpdateUserTourStatus();

  const [closed, setClosed] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const filteredSteps = useMemo(
    () =>
      filterStepsByCondition<D>(
        filterStepsByRole(steps, userRole),
        additionalData
      ).map((step): TourStep => ({...STEP_DEFAULT_PROPS, ...step})),
    [steps, additionalData]
  );

  const run = status !== 'finished' && status !== 'cancelled';

  const recordStarted = () => {
    // workaround a joyride bug which sends the request twice
    if (tourUpdateStatus.isUninitialized) {
      updateUserTourStatus({
        tourname,
        tourversion,
        status: 'started',
      });
    }
  };

  const recordFinish = () => {
    setClosed(true);
    updateUserTourStatus({
      tourname,
      tourversion,
      status: 'finished',
    });
  };

  function reportError(errorName: string, additionalData: any) {
    return reportToSentry(
      errorName,
      {
        tourname,
        tourversion,
        status,
        currentStepIndex: currentStep,
        currentStep: filteredSteps[currentStep],
        additionalData,
      },
      true
    );
  }

  const buttonsLabels = useMemo(() => {
    return {
      back: gt('Back'),
      close: gt('Close'),
      last: gt('OK'),
      next: currentStep === 0 ? gt('Start the tour') : gt('Next'),
      skip: gt('Not now'),
    };
  }, [currentStep]);

  function handleChange(eventData: CallBackProps) {
    fixIEOverlayAndArrow(eventData);
    setCurrentStep(eventData.index);
    const {action, type} = eventData;

    if (action === ACTIONS.NEXT && type === EVENTS.STEP_AFTER) {
      return recordStarted();
    }

    if (action === ACTIONS.SKIP || action === ACTIONS.CLOSE) {
      return recordFinish();
    }

    if (action === ACTIONS.NEXT && type === EVENTS.TOUR_END) {
      return recordFinish();
    }

    if (type === EVENTS.ERROR) {
      reportError('Generic error showing tour', {
        eventData,
      });
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      reportError('Tour step target not found', {
        eventData,
      });
    }
  }

  return !run ? null : (
    <ErrorBoundary name="tour">
      <Joyride
        steps={filteredSteps}
        run={run && !closed}
        scrollOffset={150}
        disableScrollParentFix={true}
        scrollToFirstStep
        callback={handleChange}
        continuous={true}
        locale={buttonsLabels}
        styles={{
          options: {
            primaryColor: colors.linkBlue,
          },
          tooltipTitle: tooltipTitleStyles,
        }}
        disableOverlayClose={true}
        showSkipButton={true}
        floaterProps={{
          disableAnimation: true,
        }}
      />
      <StyledGlobalJoyrideStyles />
    </ErrorBoundary>
  );
}

function isIEorEdge() {
  return !(
    ['chrome', 'safari', 'firefox', 'opera'].indexOf(getBrowser()) !== -1
  );
}

function fixIEOverlayAndArrow({type, action, step}: CallBackProps): void {
  if (isIEorEdge() && type === EVENTS.TOOLTIP && action === ACTIONS.UPDATE) {
    jquery('.joyride-overlay').addClass(
      `ie-${step.placement === 'center' ? 'centered' : 'positioned'}`
    );
  }

  if (getBrowser() === 'ie') {
    jquery('.__floater').addClass('ie');
    jquery('.__floater__arrow').removeAttr('style');
  }
}

function filterStepsByRole(
  steps: Array<TourStep>,
  role: 'manager' | 'teammate'
): Array<TourStep> {
  return steps.filter(
    (step): boolean => step.role === role || step.role === 'any'
  );
}

function filterStepsByCondition<D>(
  steps: Array<TourStep>,
  additionalData: D | void
): Array<TourStep> {
  return steps.filter((step) =>
    step.condition === undefined ? true : step.condition(additionalData)
  );
}

const tourSelector = createSelector<
  [
    (state: DefaultRootState) => AuthenticatedUserSerializedType['tours'],
    (state: unknown, tourName: string) => string
  ],
  UserTourType
>(
  [
    (state) => CurrentUserSelector(state).data!.tours,
    (state, tourName) => tourName,
  ],
  (tours, tourName) => {
    return (
      tours[tourName] || {
        status: 'new',
        version: 0,
      }
    );
  }
);

export const userRoleSelector = createSelector<
  [(state: DefaultRootState) => boolean],
  'manager' | 'teammate'
>([(state) => CurrentUserSelector(state).data!.is_manager], (isManager) =>
  isManager ? 'manager' : 'teammate'
);

const StyledGlobalJoyrideStyles = createGlobalStyle`
    .__floater.ie {

        &[x-placement="right"] .__floater__arrow > span {
            left: 0;
        }

        &[x-placement="bottom"] .__floater__arrow > span {
            top: 0;
        }

        &[x-placement="top"] .__floater__arrow > span {
            bottom: 0;
        }

        .__floater__body {
            > div:first-child  {
                border: 1px solid rgba(0, 0, 0, 0.25);
            }

            .__floater__arrow polygon {
                stroke: rgba(0, 0, 0, 0.25);
            }
        }
    }

    .joyride-overlay {
        &.ie-centered {
            background: rgba(0, 0, 0, 0.5);
        }

        &.ie-positioned {
            background: transparent;
        }
    }
`;

export const tooltipTitleStyles = {
  fontWeight: 'bold',
  fontSize: 15,
  textTransform: 'uppercase',
  letterSpacing: -0.25,
} as const;
